/**
 * Player interface (M2.3, FR-9.1/9.2).
 *
 * Join by name (dev seat + ticket), then a single WebSocket carries everything:
 * the board (read-only), the current prompt (never the response — FR-9.2), the
 * server-authoritative timer, scores, and one buzzer button. Buzzing is a single
 * command over the socket (FR-4.1); the button is enabled only while the buzzer
 * window is open and this player isn't locked out / already answering.
 *
 * Identity (M7): before joining, the player obtains a persistent guest identity
 * (`playerId` + a server-signed guest token) via {@link ensureGuestIdentity},
 * persisted in localStorage. The signed token — not a raw client id — is what proves
 * seat ownership on join/reconnect (FR-9.3), so a refresh keeps the same seat + score
 * (FR-2.6). In dev the token is ignored and the remembered id is honored directly.
 *
 * Reload resume (#52, prod): on a successful join we persist the durable game handle
 * ({@link savePlayerSession} — gameId + joinCode + name). On load we auto-rejoin from
 * it — for guests as well as registered players — so a reload keeps the seat instead
 * of bouncing to the join form. A present `?code=` query param overrides the stored
 * code (a fresh invite wins); the session is cleared on GAME_OVER and on a failed
 * rejoin so a stale handle self-heals to the form. Mirrors the host's
 * {@link ../net/host-session} restore.
 */

import { gamePhaseOfOrNull } from '@jeopardy/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { POWERUP_KINDS, POWERUP_SPECS, gamePhaseOf } from '@jeopardy/shared';
import type { PowerUpKind, PowerUpTiming, PowerUpTokens } from '@jeopardy/shared';
import { joinAsPlayer, resolveGameByCode } from '../net/game-api';
import { withJoinRetry, shouldClearSessionOnJoinFailure } from '../net/join-retry';
import { IS_PROD_DEPLOY } from '../config';
import { ensureGuestIdentity, loadIdentity, recoverStaleIdentity } from '../net/auth';
import {
  clearPlayerSession,
  loadPlayerSession,
  savePlayerSession,
} from '../net/player-session';
import { SignIn } from '../ui/SignIn';
import { useGameConnection } from '../net/use-game';
import { recordBuzzRtt } from '../net/rum';
import { AppHeader } from '../ui/AppHeader';
import { HIDDEN_PERIL, FINAL_PERIL } from '../ui/brand';
import { BirthYearPrompt } from '../ui/BirthYearPrompt';
import { Board } from '../ui/Board';
import { Buzzer } from '../ui/Buzzer';
import { CategoryPreferences } from '../ui/CategoryPreferences';
import { CluePanel } from '../ui/CluePanel';
import { ClueReveal } from '../ui/ClueReveal';
import { FinalJudgingAnswer } from '../ui/FinalJudgingAnswer';
import { FinalReveal } from '../ui/FinalReveal';
import { DisputePanel } from '../ui/DisputePanel';
import { FinalAnswerInput } from '../ui/FinalAnswerInput';
import { homeGuard } from '../ui/leave-guard';
import { GameOver } from '../ui/GameOver';
import { PostGameFeedback } from '../ui/PostGameFeedback';
import { GenerationStatus, GenerationFailedBanner } from '../ui/GenerationStatus';
import { Lobby } from '../ui/Lobby';
import {
  BrainFreezeNotice,
  ComebackUnlock,
  DoubleUpArmedBanner,
  InsuranceArmedBanner,
  NewCardsBanner,
  PowerUpHand,
  SnakeBiteNotice,
  SneakPeekHintView,
  TagTeamCalledNotice,
  WagerBoostRoll,
  useFreshEvent,
} from '../ui/PowerUpHand';
import { Scoreboard } from '../ui/Scoreboard';
import { SpokenAnswer } from '../ui/SpokenAnswer';
import { TimerView } from '../ui/TimerView';
import { WagerInput } from '../ui/WagerInput';
import { useClueReader } from '../voice/use-clue-reader';
import { useDocumentHead } from '../seo/useDocumentHead';
import { headModelFor } from '../seo/route-seo';
import {
  categoryNameForClue,
  clueReveal,
  currentClue,
  finalJudgingAnswer,
  finalRevealResolvesAt,
  finalRevealResponse,
  isActivePlayPhase,
  isClueOpen,
  myDailyDoubleWager,
  myFinalWager,
  revealedResponse,
  shouldTypeFinalAnswer,
} from '../game/selectors';
import {
  answerBeingRead,
  clueBeingRead,
  finalAnswerBeingRead,
  isAutoHost,
  isJudging,
} from '../game/voice-selectors';

const PLAYER_ID_KEY = 'jeopardy.playerId';

/**
 * #311 (NFR-6.12): how long after the authoritative "someone else won" frame arrives
 * to keep this loser's buzzer button click-live under the hood, so a press already
 * on its way from the player still journals as a `BuzzAttempt` loser. Small enough
 * that any UX flicker is imperceptible (the label stays "Wait…" throughout), large
 * enough to cover typical human reaction slop past the client's own lockout. Behavior
 * is unchanged — the server still decides the winner by arrival order (FR-4.2); the
 * server's existing `!windowOpen` branch records the late press as a loser (telemetry
 * only). See the `onLateRacePress` doc on {@link Buzzer} for the swallowed-click cause.
 */
const LATE_RACE_GRACE_MS = 150;

/** #232 v2 — the wager-step hand only offers Wager Boost. */
const WAGER_ONLY_WINDOWS: Record<PowerUpTiming, boolean> = {
  PRE_BUZZ: false,
  REOPENED_BUZZ: false,
  POST_BUZZ: false,
  WAGER_STEP: true,
};
/** #232 v2 — during Final wagering, Insurance (PRE_BUZZ) + Wager Boost (WAGER_STEP) are legal. */
const FINAL_WAGER_WINDOWS: Record<PowerUpTiming, boolean> = {
  PRE_BUZZ: true,
  REOPENED_BUZZ: false,
  POST_BUZZ: false,
  WAGER_STEP: true,
};

export function PlayerPage(): React.JSX.Element {
  // Per-route SEO (#241, FR-15.4): self-canonical + join-intent head (was inheriting the
  // landing shell's `canonical=/`).
  useDocumentHead(headModelFor('/player'));
  const [ticket, setTicket] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [name, setName] = useState('');
  // Join code, seeded from a `?code=` query param so a host's shared link
  // (`/player?code=ABCDE-...`) prefills it. Falling back to a persisted player session
  // (#52) so a reload without the code in the URL — e.g. the code was typed into the
  // form — still targets the right game. The query param wins when present (a fresh
  // invite overrides a stored session). In dev the code is ignored (single game).
  const [code, setCode] = useState(() => {
    const fromQuery =
      typeof location !== 'undefined'
        ? new URLSearchParams(location.search).get('code')
        : null;
    if (fromQuery) return fromQuery;
    const saved = IS_PROD_DEPLOY ? loadPlayerSession() : null;
    return saved?.joinCode ?? '';
  });
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  /**
   * A registered player already has a name — no entry needed. Prefer their editable
   * display name (FR-2.12) and fall back to the login username when unset.
   */
  const registered = loadIdentity();
  const registeredName =
    registered && !registered.isGuest
      ? (registered.preferredName ?? registered.displayName)
      : undefined;

  // Reload resume (#52, prod): the persisted game handle from a prior join. A guest —
  // who has no username to auto-join under — gets their display name from here; both
  // guests and registered players get the join code fallback (via `code`, above).
  // Recomputed each render, so clearing it (game over / failed rejoin) drops the
  // "Rejoining…" state on the next render.
  const savedSession = IS_PROD_DEPLOY ? loadPlayerSession() : null;
  // The name to auto-(re)join under: the registered username, else a guest's
  // remembered name. When set (and not yet ticketed) we auto-join rather than showing
  // the form. A guest only ever has a saved name when a session exists, which always
  // carries a join code — so the auto-join always has a target game.
  const autoJoinName = registeredName ?? savedSession?.name;
  /** A guest (no username) mid auto-rejoin: show progress, not the empty form. */
  const guestRejoining = !registeredName && Boolean(savedSession);

  // Guards the auto-(re)join effect against a re-fire loop. Re-armed (below) when a
  // transient rejoin failure keeps the session, so a later render can resume the seat.
  const attempted = useRef(false);

  const join = useCallback(
    async (joinName: string, joinCode: string, opts?: { autoRejoin?: boolean }) => {
      setJoinError(null);
      setJoining(true);
      try {
        // In prod, resolve the host's join code → the specific gameId, so the player
        // joins THAT game and not the demo game (M8). A blank code (or dev) → the
        // default game. An unknown code throws a friendly message here. The code itself
        // is forwarded to the join call as the required entry check (M11.5.2).
        const trimmedCode = joinCode.trim();
        const codeForJoin = IS_PROD_DEPLOY && trimmedCode ? trimmedCode : undefined;

        // One join attempt — resolve the code → gameId, prove identity, seat + mint
        // ticket — wrapped in a bounded retry (#67). On a ≥2-task fleet the player can
        // land on a non-owner task mid-convergence; the server signals those blips as
        // retryable (404-by-code / 409 / 503) and this honors them instead of failing
        // on the first transient. A definitive error (bad code, rejected token) or
        // budget exhaustion rethrows unchanged.
        const { player, gameId } = await withJoinRetry(async () => {
          const resolvedGameId =
            IS_PROD_DEPLOY && trimmedCode
              ? await resolveGameByCode(trimmedCode)
              : undefined;

          // Prefer a signed guest identity (prod, M7 — FR-9.3). If the guest endpoint
          // isn't available (local dev server), fall back to the remembered raw id so
          // the dev flow keeps working (FR-2.6, dev-grade).
          let identity: { guestToken?: string; playerId?: string };
          try {
            const guest = await ensureGuestIdentity();
            identity = { guestToken: guest.guestToken, playerId: guest.playerId };
          } catch {
            identity = { playerId: localStorage.getItem(PLAYER_ID_KEY) ?? undefined };
          }
          try {
            const seated = await joinAsPlayer(
              joinName.trim(),
              identity,
              resolvedGameId,
              codeForJoin,
            );
            return { player: seated, gameId: resolvedGameId };
          } catch (err) {
            // A rejected identity token (stale stored token / rotated TICKET_SECRET)
            // would otherwise livelock a returning player on a dead token. Recover once
            // by minting a fresh guest identity, then retry the join (M7 review fix).
            if (err instanceof Error && /identity token/i.test(err.message)) {
              const fresh = await recoverStaleIdentity();
              const seated = await joinAsPlayer(
                joinName.trim(),
                { guestToken: fresh.guestToken, playerId: fresh.playerId },
                resolvedGameId,
                codeForJoin,
              );
              return { player: seated, gameId: resolvedGameId };
            }
            throw err;
          }
        });
        localStorage.setItem(PLAYER_ID_KEY, player.playerId);
        setPlayerId(player.playerId);
        setTicket(player.ticket);
        // Persist the durable game handle so a reload auto-rejoins this game + seat
        // (#52). Only when we joined a SPECIFIC prod game (a real resolved gameId +
        // code); a dev join, or a prod join with no code, has no game to remember.
        if (IS_PROD_DEPLOY && gameId && trimmedCode) {
          savePlayerSession({ gameId, joinCode: trimmedCode, name: joinName.trim() });
        }
      } catch (err) {
        // A still-converging owner (transient 409/503 that outlasted the retry budget)
        // on an AUTO-rejoin must NOT bounce a returning player to the join form (#67):
        // keep the seat handle and leave the "Rejoining…" progress up (no scary error).
        // Re-arm `attempted` so a remount/reload (or a dep change) can resume the seat —
        // the in-mount recovery is `withJoinRetry`'s own bounded attempts. Everything
        // else surfaces the
        // error; a failed auto-rejoin with a DEFINITIVE cause (stale/reaped handle, bad
        // code) also drops the handle so the next render self-heals to the form.
        if (opts?.autoRejoin && IS_PROD_DEPLOY && !shouldClearSessionOnJoinFailure(err)) {
          attempted.current = false;
        } else {
          setJoinError(err instanceof Error ? err.message : 'Join failed');
          if (opts?.autoRejoin && IS_PROD_DEPLOY) clearPlayerSession();
        }
      } finally {
        setJoining(false);
      }
    },
    [],
  );

  // Auto-(re)join on load (M7 registered UX + #52 reload resume). A registered player
  // joins under their username — skipping the name form AND the sign-in button (once
  // you have an account, your username IS your player name). A guest with a persisted
  // session (#52) rejoins under their remembered name + code. A first-time guest (no
  // session) has no `autoJoinName`, so they still see the join form. `attempted`
  // guards against a re-fire loop if the join errors (e.g. the name is already taken).
  useEffect(() => {
    if (autoJoinName && !ticket && !attempted.current) {
      attempted.current = true;
      void join(autoJoinName, code, { autoRejoin: true });
    }
  }, [autoJoinName, ticket, join, code]);

  const { snapshot, status, send, scheduledOpenClueId } = useGameConnection(ticket);

  // Auto-host (#91): read the clue aloud on this client while it's in READING, then
  // report `read_complete`. A no-op in HUMAN games and where TTS is unsupported.
  useClueReader({ state: snapshot.state, send });

  // Once the game is over, drop the persisted session so a later reload returns to the
  // join form rather than trying to rejoin a finished game (which may also be reaped
  // server-side — a rejoin would then fail anyway). Mirrors the host's GAME_OVER clear.
  useEffect(() => {
    if (IS_PROD_DEPLOY && gamePhaseOfOrNull(snapshot.state) === 'GAME_OVER')
      clearPlayerSession();
  }, [gamePhaseOfOrNull(snapshot.state)]);

  // #232: a comeback/earned grant addressed to ME → a brief "you unlocked a card!"
  // celebration (D4: never says why). Computed here so the hook runs every render,
  // before the (conditional) join-form early return below. Only the viewer's own grants
  // are shown; another player's grant is ignored.
  const grantEvent = snapshot.lastPowerupGranted;
  const myGrant =
    snapshot.state?.jeopardy.powerups &&
    grantEvent &&
    playerId !== null &&
    grantEvent.playerId === playerId
      ? grantEvent
      : null;
  const freshGrant = useFreshEvent(myGrant);
  // #232 v2 transient cues — hooks run every render (before the early returns below).
  // Brain Freeze is OPEN (shown to the frozen); Snake Bite reveal + the Wager Boost die
  // are owner-only (the server sends them only to the affected player). Consumed in the
  // in-game view; scoped to the current clue there.
  const powerupsPresent = snapshot.state?.jeopardy.powerups !== undefined;
  const freshFreeze = useFreshEvent(powerupsPresent ? snapshot.lastBrainFreeze : null);
  // #232 v2 (item 4) — render the Wager Boost roll from the PERSISTENT, clue-scoped
  // snapshot.wagerBoost (kept by keepWagerBoost until the clue / Final wager ends), not a
  // 6s transient, so the "wager × N = payout" preview stays up for the whole clue.
  const wagerBoost = powerupsPresent ? snapshot.wagerBoost : null;
  // #232 v2 — Brain Freeze visibly CHILLS my buzzer: when a fresh freeze arrives and
  // I'm not the caster, disable my buzzer for `freezeMs` so it's clear why racing is
  // pointless (instead of a live button that says "You buzzed!" then loses).
  const [frozenActive, setFrozenActive] = useState(false);
  useEffect(() => {
    if (!freshFreeze || freshFreeze.casterId === playerId) return;
    setFrozenActive(true);
    const t = setTimeout(() => setFrozenActive(false), freshFreeze.freezeMs);
    return () => clearTimeout(t);
  }, [freshFreeze, playerId]);
  // Surface a server rejection to the player (was silent — a rejected play just looked
  // "inert", e.g. Tag-Team below its 3-player floor). A `game_state` clears `lastError`;
  // this also auto-dismisses after a few seconds if no state follows (a pure reject).
  const [errorNote, setErrorNote] = useState<{ code: string; message: string } | null>(
    null,
  );
  useEffect(() => {
    if (!snapshot.lastError) return;
    setErrorNote(snapshot.lastError);
    const t = setTimeout(() => setErrorNote(null), 4000);
    return () => clearTimeout(t);
  }, [snapshot.lastError]);
  // #232 v2 — a persistent "you got a new card" surface. The 4s unlock pop is easy to
  // miss: grants often land BETWEEN rounds (no clue open → the hand isn't rendered), and a
  // multi-player grant batch clobbers `lastPowerupGranted` so a non-last recipient sees no
  // pop at all. Diff MY own token counts across snapshots — any kind whose count rose is
  // "new" until I next open a clue (where the hand is visible). Robust to batch + timing.
  const myTokensNow: PowerUpTokens =
    (playerId !== null
      ? snapshot.state?.players.find((p) => p.id === playerId)?.tokens
      : undefined) ?? {};
  const prevTokensRef = useRef<PowerUpTokens | null>(null);
  const openClueId = snapshot.state?.jeopardy.clueState?.clueId;
  const [newCardKinds, setNewCardKinds] = useState<PowerUpKind[]>([]);
  useEffect(() => {
    if (!powerupsPresent) return;
    const prev = prevTokensRef.current;
    prevTokensRef.current = myTokensNow;
    if (prev === null) return; // first sighting — set a baseline, never celebrate on load
    const gained = POWERUP_KINDS.filter((k) => (myTokensNow[k] ?? 0) > (prev[k] ?? 0));
    if (gained.length > 0) {
      setNewCardKinds((cur) => Array.from(new Set([...cur, ...gained])));
    }
  }, [myTokensNow, powerupsPresent]);
  // Clear the "new" marker once a clue is open — the hand is on screen, so it's now seen.
  useEffect(() => {
    if (openClueId !== undefined) setNewCardKinds([]);
  }, [openClueId]);
  // Buzzer-smash lockout (#198, FR-4.11) — CLIENT-SIDE anti-cheat state. These hooks
  // must run on every render (before any early return), so they live up here even
  // though they're only consumed in the in-game view below. Smashing the buzzer during
  // the pre-open "Wait…" window is detected and enforced entirely on this client: a
  // `disabled` button can't be smashed (it swallows the events), so the Buzzer stays
  // tappable while waiting (under `smash-lockout`) and each press is counted here. Once
  // the count for THIS clue reaches the configured limit we lock ourselves out of the
  // clue — no server round-trip — so a smasher simply can't buzz when the window opens.
  const [smashLockedClueId, setSmashLockedClueId] = useState<string | null>(null);
  const smashCount = useRef<{ clueId: string | null; count: number }>({
    clueId: null,
    count: 0,
  });
  const registerEarlySmash = useCallback(() => {
    const st = snapshot.state;
    const cid = st?.jeopardy.clueState?.clueId;
    if (cid === undefined) return;
    // Fresh clue → fresh count (a stale count from a prior clue must never bleed over).
    if (smashCount.current.clueId !== cid) smashCount.current = { clueId: cid, count: 0 };
    smashCount.current.count += 1;
    // Floor the limit at 1 defensively (a misconfigured 0/negative would never lock
    // out); the resolved default is 3 — tolerate two accidental taps, lock on the third.
    const limit = Math.max(1, st?.rules.earlyBuzzSmashLimit ?? 3);
    if (smashCount.current.count >= limit) setSmashLockedClueId(cid);
  }, [snapshot.state]);

  // #247 (NFR-6.4 / NFR-6.7): buzz round-trip + press-time capture. `buzzSentAtRef` holds
  // the local monotonic time this player's buzz was sent (the RTT start + the advisory
  // press timestamp); `buzzClueRef` scopes it to the clue it was sent for. Hooks run every
  // render (before the early returns below), so they live up here.
  const buzzSentAtRef = useRef<number | null>(null);
  const buzzClueRef = useRef<string | null>(null);
  // Capture the press instant AT the input event, send it as advisory telemetry (the server
  // normalizes it for would-flip; it NEVER decides the winner — FR-4.2), and start the RTT
  // clock. Called from the Buzzer's onBuzz below.
  const handleBuzz = useCallback(() => {
    const pressedAtClientMonoMs = performance.now();
    buzzSentAtRef.current = pressedAtClientMonoMs;
    buzzClueRef.current = snapshot.state?.jeopardy.clueState?.clueId ?? null;
    send({ type: 'buzz', pressedAtClientMonoMs });
  }, [send, snapshot.state?.jeopardy.clueState?.clueId]);
  // Wire the `recordBuzzRtt` RUM seam (NFR-6.4): stop the clock at the FIRST authoritative
  // clueState transition after our buzz — win (we became the active responder), or lose
  // (someone else became active, we got locked out, or the clue moved on).
  const buzzClueState = snapshot.state?.jeopardy.clueState;
  const buzzGameId = snapshot.state?.gameId;
  useEffect(() => {
    const sentAt = buzzSentAtRef.current;
    const forClue = buzzClueRef.current;
    if (sentAt === null || forClue === null || playerId === null) return;
    const cs = buzzClueState;
    const settled =
      !cs ||
      cs.clueId !== forClue ||
      cs.activePlayerId !== undefined ||
      cs.lockedOutPlayerIds.includes(playerId);
    if (!settled) return;
    const outcome = cs?.activePlayerId === playerId ? 'win' : 'lose';
    recordBuzzRtt({
      latencyMs: performance.now() - sentAt,
      outcome,
      ...(buzzGameId ? { gameId: buzzGameId } : {}),
    });
    buzzSentAtRef.current = null;
    buzzClueRef.current = null;
  }, [buzzClueState, buzzGameId, playerId]);
  // #311 (NFR-6.12): open a short "late-race" grace on this clue when we FIRST see the
  // authoritative winner land (activePlayerId defined, not us, not already locked out).
  // Downstream this keeps the Buzzer's click-live for {@link LATE_RACE_GRACE_MS} so a
  // press already on the way from a losing player still journals as a `BuzzAttempt` — the
  // disabled-button-swallows-the-press bias the calibration signal would otherwise carry
  // (only losers whose press landed BEFORE the winning frame reached them get measured).
  // Behavior-neutral: the server decides the winner by arrival order (FR-4.2); the late
  // press is routed through the existing `!windowOpen` → `recordBuzzLoser` path. Scoped
  // by clueId so a fresh clue starts fresh; auto-clears via the same-clueId setState.
  const [lateGraceClueId, setLateGraceClueId] = useState<string | null>(null);
  const lateGraceClueIdInGame = buzzClueState?.clueId;
  const lateGraceActivePlayerId = buzzClueState?.activePlayerId;
  const lateGraceServerLockedOut =
    playerId !== null
      ? (buzzClueState?.lockedOutPlayerIds.includes(playerId) ?? false)
      : false;
  useEffect(() => {
    if (
      lateGraceClueIdInGame === undefined ||
      lateGraceActivePlayerId === undefined ||
      playerId === null ||
      lateGraceActivePlayerId === playerId || // I won — no grace needed
      lateGraceServerLockedOut // already forfeited this clue server-side
    ) {
      return;
    }
    // Arm grace on this clue's transition to "someone else won"; the timer runs off the
    // authoritative state change (React batches after the game_state broadcast lands).
    setLateGraceClueId(lateGraceClueIdInGame);
    const clueId = lateGraceClueIdInGame;
    const t = setTimeout(() => {
      setLateGraceClueId((cur) => (cur === clueId ? null : cur));
    }, LATE_RACE_GRACE_MS);
    return () => clearTimeout(t);
  }, [
    lateGraceClueIdInGame,
    lateGraceActivePlayerId,
    playerId,
    lateGraceServerLockedOut,
  ]);

  if (!ticket) {
    // Registered player: no name entry, no sign-in button — auto-joining under
    // their username (the effect above fires the join). Just show progress / any
    // error (e.g. that username is already seated in this game).
    if (registeredName) {
      return (
        <div className="app-shell">
          <AppHeader role="Player" />
          <div className="join-form">
            <h1 className="app-title">Joining as {registeredName}…</h1>
            {joinError && (
              <div className="error-text" data-testid="join-error">
                {joinError}
              </div>
            )}
          </div>
        </div>
      );
    }
    // Guest with a persisted session (#52): auto-rejoining under their remembered
    // name (the effect above fires it). Show progress instead of the empty form so a
    // reload doesn't flash "Join the game". A failed rejoin clears the session, so the
    // next render falls through to the form below (with any error).
    if (guestRejoining && !joinError) {
      return (
        <div className="app-shell">
          <AppHeader role="Player" />
          <div className="join-form">
            <h1 className="app-title" data-testid="rejoining">
              Rejoining…
            </h1>
          </div>
        </div>
      );
    }
    return (
      <div className="app-shell">
        <AppHeader role="Player" />
        <form
          className="join-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void join(name, code);
          }}
        >
          <h1 className="app-title">Join the game</h1>
          {/* Join code the host shares (prod). Prefilled from `?code=` in a shared
              link. Hidden in dev, where there's a single game and no code. */}
          {IS_PROD_DEPLOY && (
            <input
              aria-label="Join code"
              data-testid="join-code"
              placeholder="Join code (from the host)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          )}
          <input
            aria-label="Your name"
            data-testid="join-name"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus={!IS_PROD_DEPLOY}
          />
          <button
            type="submit"
            className="btn btn--primary"
            data-testid="join-submit"
            disabled={
              joining || name.trim().length === 0 || (IS_PROD_DEPLOY && !code.trim())
            }
          >
            {joining ? 'Joining…' : 'Join'}
          </button>
          {joinError && (
            <div className="error-text" data-testid="join-error">
              {joinError}
            </div>
          )}
        </form>
        {/* Optional registered sign-in (M7.2): guests can play without it, but
            signing in carries identity + stats across devices (FR-2.2/2.3). */}
        <SignIn />
      </div>
    );
  }

  const state = snapshot.state;
  const clue = currentClue(state);
  const clueOpen = isClueOpen(state);
  const clueState = state?.jeopardy.clueState;
  const buzzerWindowOpen = clueState?.status === 'BUZZER_OPEN';
  // #264 (FR-4.13, S4): a synced client unlocks its buzzer locally at the scheduled `OPEN_AT`,
  // ahead of the authoritative `buzzer_opened` frame's delivery. ORed into `canBuzz` ONLY — NOT
  // into `buzzerWindowOpen`, which also gates `armEarlySmash` + the REOPENED_BUZZ window (those
  // must stay on the authoritative status). Matched by clueId so it auto-invalidates on a clue
  // change. The server still decides the winner by arrival order (FR-4.2) and rejects genuinely
  // pre-`OPEN_AT` buzzes (D-264.5), so this can only unlock at/after the shared instant.
  const locallyScheduledOpen =
    scheduledOpenClueId !== null && scheduledOpenClueId === clueState?.clueId;
  // Buzz state is derived from AUTHORITATIVE clueState (rendered by version), not
  // from transient per-client events — one truth, no event/state race (DESIGN §5.3).
  const iAmActive = playerId !== null && clueState?.activePlayerId === playerId;
  const serverLockedOut =
    playerId !== null && (clueState?.lockedOutPlayerIds.includes(playerId) ?? false);

  // Buzzer-smash lockout (#198, FR-4.11): I'm smash-locked out of the currently open
  // clue (client-only, tracked above), which forfeits it just like a server lockout and
  // drives the explanation banner below.
  const smashPolicy = state?.rules.earlyBuzzPolicy === 'smash-lockout';
  const smashLockedOut =
    smashLockedClueId !== null && clueState?.clueId === smashLockedClueId;
  const iAmLockedOut = serverLockedOut || smashLockedOut;

  // #232 v2 — I was Snake-Bitten this clue (a landed, stealth hard-prevent). Persistent
  // for the clue (the store keeps the reveal keyed to the open clue), so my buzzer stays
  // blocked once I've been bitten — not just for the transient animation window.
  const iWasBitten =
    snapshot.lastSnakeBite != null && snapshot.lastSnakeBite.clueId === clueState?.clueId;
  // Brain Freeze notice shows for the (readable) freshFreeze window (~4s) to everyone
  // but the caster; the buzzer stays disabled only for the ~750ms `frozenActive` chill.
  const iAmFrozen =
    freshFreeze != null &&
    freshFreeze.casterId !== playerId &&
    freshFreeze.clueId === clueState?.clueId;
  const canBuzz =
    clueOpen &&
    (buzzerWindowOpen || locallyScheduledOpen) && // #264: authoritative OR local scheduled open
    !iAmLockedOut &&
    !frozenActive && // Brain Freeze: my buzzer is chilled for ~0.75s
    !iWasBitten && // Snake Bite: I'm blocked this clue
    clueState?.activePlayerId === undefined;
  // Arm client-side smash detection during the pre-open "Wait…" window when the host
  // opted into `smash-lockout`. (Under 'ignore'/'lockout' the Wait button stays inert —
  // there is nothing to count on this client.) Disarmed once I hold the buzzer or am
  // already locked out.
  const armEarlySmash =
    smashPolicy &&
    clueOpen &&
    !buzzerWindowOpen &&
    !iAmLockedOut &&
    !iAmActive &&
    clueState?.activePlayerId === undefined;
  // #311 (NFR-6.12): arm the late-race grace when we're in the small post-award window
  // for THIS clue and we're not the winner / not locked out / not frozen or bitten. The
  // grace timer is set upstream (see the useEffect near `lateGraceClueId`); here we only
  // gate the click-live behavior on that timer AND the live clue predicates so a stale
  // grace can never bleed into an unrelated clue.
  const armLateRace =
    lateGraceClueId !== null &&
    lateGraceClueId === clueState?.clueId &&
    clueState?.activePlayerId !== undefined &&
    clueState.activePlayerId !== playerId &&
    !iAmActive &&
    !iAmLockedOut &&
    !frozenActive &&
    !iWasBitten;
  // Auto-host (#91, D10): there is no human host, so the board-control player picks
  // the next clue from their own board. Server-authorized (handleSelectClue allows
  // the board-control player); gated to AUTO so human games keep host-only selection.
  const iSelectClues =
    isAutoHost(state) &&
    state?.jeopardy.phase === 'IN_ROUND' &&
    playerId !== null &&
    state.jeopardy.boardControlPlayerId === playerId;

  // M3 contextual single-action prompts (each shown only when legal for me).
  const ddWager = myDailyDoubleWager(state, playerId);
  const finalWager = myFinalWager(state, playerId);
  const typeFinalAnswer = shouldTypeFinalAnswer(state, playerId);
  const inFinal =
    gamePhaseOfOrNull(state) === 'FINAL_WAGER' ||
    gamePhaseOfOrNull(state) === 'FINAL_CLUE' ||
    gamePhaseOfOrNull(state) === 'FINAL_JUDGING' ||
    gamePhaseOfOrNull(state) === 'FINAL_REVEAL';
  // #205 (FR-6.6, amended by #344): the Final correct response, revealed to this device
  // once the reveal fires (first-correct ruling, or the all-wrong linger) and held through
  // FINAL_REVEAL. Null while it is being held for suspense during early FINAL_JUDGING.
  const finalResponse = finalRevealResponse(state);
  // #344 (FR-6.7): the currently-rated contestant's name + submitted answer, shown to the
  // room during FINAL_JUDGING (null outside it / once everyone is judged).
  const judgingAnswer = finalJudgingAnswer(state);
  // #192 (FR-4.10): the open clue resolved — show the reveal band / award cue in
  // place of the buzzer for the brief hold.
  const reveal = clueReveal(state);
  const awardedName =
    reveal?.awardedPlayerId !== undefined
      ? state?.players.find((p) => p.id === reveal.awardedPlayerId)?.name
      : undefined;

  // #232 power-ups (Game Night only): everything below no-ops when `powerups` is absent
  // (Classic), so a Classic game renders exactly as before.
  const powerups = state?.jeopardy.powerups;
  const powerupsOn = powerups !== undefined;
  const me =
    playerId !== null ? state?.players.find((p) => p.id === playerId) : undefined;
  const myTokens = me?.tokens ?? {};
  // Pre-buzz window (the only time a v1 card is playable): AUTO reads in READING; a HUMAN
  // game waits in LOCKED with nobody holding the buzzer (§2 per-mode predicate). Never on
  // a Daily Double (no buzz race → Double Up/Sneak Peek disabled there).
  const preBuzzWindow =
    powerupsOn &&
    gamePhaseOfOrNull(state) === 'CLUE_OPEN' &&
    clueState !== undefined &&
    clueState.mode !== 'DAILY_DOUBLE' &&
    (isAutoHost(state)
      ? clueState.status === 'READING'
      : clueState.status === 'LOCKED' && clueState.activePlayerId === undefined);
  // #232 v2 — per-timing-class play windows for the board clue. WAGER_STEP is handled
  // in the Daily-Double / Final wager branches (their own hand render), so it's false
  // here. REOPENED_BUZZ is Steal (a rebound after a wrong answer); POST_BUZZ is
  // Tag-Team (while I hold the buzzer). Race cards never apply on a Daily Double.
  const notDailyDouble = clueState !== undefined && clueState.mode !== 'DAILY_DOUBLE';
  const boardWindows: Record<PowerUpTiming, boolean> = {
    PRE_BUZZ: Boolean(preBuzzWindow),
    REOPENED_BUZZ: Boolean(
      powerupsOn && buzzerWindowOpen && clueState?.reopened === true && notDailyDouble,
    ),
    POST_BUZZ: Boolean(
      powerupsOn && clueState?.status === 'LOCKED' && iAmActive && notDailyDouble,
    ),
    WAGER_STEP: false,
  };
  // Armed cards I can see on this clue (Snake Bite is stealth + redacted, so never here).
  const myArmed: PowerUpKind[] = [];
  if (playerId !== null && clueState) {
    if (clueState.doubleUpBy?.includes(playerId)) myArmed.push('DOUBLE_UP');
    if (clueState.peekedBy?.includes(playerId)) myArmed.push('SNEAK_PEEK');
    if (clueState.brainFreezeBy?.includes(playerId)) myArmed.push('BRAIN_FREEZE');
    if (clueState.insuredBy?.includes(playerId)) myArmed.push('INSURANCE');
    if (clueState.tagTeamBy?.includes(playerId)) myArmed.push('TAG_TEAM');
    if (clueState.wagerBoostBy?.includes(playerId)) myArmed.push('WAGER_BOOST');
  }
  // Final-Jeopardy armed cards live on `final`, not `clueState`.
  if (playerId !== null && state?.jeopardy.final) {
    if (state.jeopardy.final.insuredBy?.includes(playerId)) myArmed.push('INSURANCE');
    if (state.jeopardy.final.wagerBoostBy?.includes(playerId))
      myArmed.push('WAGER_BOOST');
  }
  // Other seats I may target (Snake Bite victim / Tag-Team teammate).
  const powerupTargets =
    state?.players
      .filter((p) => p.id !== playerId)
      .map((p) => ({ id: p.id, name: p.name })) ?? [];
  // Send a play with the current open clue's id (Final uses '' — the server ignores it).
  const playPowerup = (powerup: PowerUpKind, target?: string): void =>
    send({
      type: 'play_powerup',
      powerup,
      clueId: clueState?.clueId ?? '',
      ...(target !== undefined ? { target } : {}),
    });
  // #232 v2 — `frozenActive` (Brain Freeze, chilled window) and `iWasBitten` (Snake Bite,
  // blocked this clue) are computed above; both disable the buzzer + drive a notice.
  // #232 v2 — I was tagged in as a Tag-Team teammate on this clue → a notice on MY phone
  // (the caster keeps the buzzer + points; I'm asked to help). Guarded by clue so a stale
  // play from an earlier clue doesn't linger.
  const taggedPlay = snapshot.lastPowerupPlayed;
  const taggedInFrom =
    taggedPlay?.powerup === 'TAG_TEAM' &&
    taggedPlay.target === playerId &&
    taggedPlay.clueId === clueState?.clueId
      ? (state?.players.find((p) => p.id === taggedPlay.playerId)?.name ?? 'A teammate')
      : null;
  // Only the viewer's own peek, and only while its clue is still open (the store keys it
  // by clueId — this guard is belt-and-suspenders against a stale render).
  const sneakPeek =
    snapshot.sneakPeek && clueState && snapshot.sneakPeek.clueId === clueState.clueId
      ? snapshot.sneakPeek
      : null;

  return (
    <div className="app-shell">
      {/* Mid-play, confirm before leaving so a mis-tap doesn't yank the player out of
          the live view (FR-9.8 / D-F8.2). The seat is kept — they can rejoin. */}
      <AppHeader
        role="Player"
        status={status}
        onHomeIntercept={homeGuard(
          isActivePlayPhase(state),
          'Leave the game? Your seat is kept — you can rejoin from home.',
        )}
      />

      {!snapshot.hasState && <p className="muted">Connecting to the game…</p>}

      {/* Surface a server rejection (e.g. a play that isn't legal yet) so nothing fails
          silently. Auto-dismisses; announced to assistive tech. */}
      {errorNote && (
        <div
          className="player-error"
          role="status"
          aria-live="polite"
          data-testid="player-error"
        >
          {errorNote.message}
        </div>
      )}

      {state &&
        (gamePhaseOf(state) === 'LOBBY' || gamePhaseOf(state) === 'GENERATING') && (
          <div className="layout-two-col">
            <div className="center-col">
              {/* Registered players with no birth year on file: a soft nudge to set one
                for age-appropriate generation (FR-2.8). Dismissible, never blocks play. */}
              <BirthYearPrompt />
              {/* Generation failed (FR-9.9): players see the read-only reason (no
                recovery action — only the host can retry). */}
              {snapshot.lastGenerationFailure && gamePhaseOf(state) === 'LOBBY' && (
                <GenerationFailedBanner
                  code={snapshot.lastGenerationFailure.code}
                  message={snapshot.lastGenerationFailure.message}
                />
              )}
              <Lobby players={state.players} mode={state.mode} lobby={state.lobby} />
              {/* Auto-host (#91): no human host, so a PLAYER starts the game (a PACK
                board is ready immediately; DYNAMIC starts via generation). One control. */}
              {isAutoHost(state) &&
                gamePhaseOf(state) === 'LOBBY' &&
                state.mode === 'PACK' && (
                  <button
                    type="button"
                    className="btn btn--primary"
                    data-testid="auto-start-game"
                    onClick={() => send({ type: 'start_game' })}
                  >
                    Start game
                  </button>
                )}
              {/* Dynamic + still in LOBBY: submit/update my category wishes (idempotent).
                Guests may self-report an age band; registered players' bands are
                derived server-side (M12 §6.2), so the select is hidden for them. */}
              {gamePhaseOf(state) === 'LOBBY' && state.mode === 'DYNAMIC' && (
                <CategoryPreferences
                  gameId={state.gameId}
                  initialCategories={
                    playerId ? state.lobby?.preferences[playerId]?.categories : undefined
                  }
                  allowAgeBand={registered ? registered.isGuest : true}
                  onSubmit={(categories, ageBand) =>
                    send(
                      ageBand
                        ? { type: 'set_category_preferences', categories, ageBand }
                        : { type: 'set_category_preferences', categories },
                    )
                  }
                />
              )}
              {gamePhaseOf(state) === 'GENERATING' && state.generation && (
                <GenerationStatus generation={state.generation} />
              )}
            </div>
            <div className="stack">
              <Scoreboard
                players={state.players}
                boardControlPlayerId={state.jeopardy.boardControlPlayerId}
                clueState={state.jeopardy.clueState}
                selfPlayerId={playerId ?? undefined}
              />
            </div>
          </div>
        )}

      {state && gamePhaseOf(state) !== 'LOBBY' && gamePhaseOf(state) !== 'GENERATING' && (
        <div className="layout-two-col">
          <div className="center-col">
            {/* #232: a fresh grant to me → a brief celebration (never the deficit reason).
                Sits above the clue so it's seen wherever play currently is. */}
            {freshGrant && <ComebackUnlock powerup={freshGrant.powerup} />}
            {/* #232 v2: a PERSISTENT "you got a new card" banner (survives the 4s pop and
                shows for grants that land between rounds, when the hand isn't rendered).
                Cleared once a clue opens and the hand becomes visible. */}
            {newCardKinds.length > 0 && <NewCardsBanner kinds={newCardKinds} />}
            {/* #232 v2: the Wager Boost die — rolled once MY wager locked. Shown here
                (above the play area) so it lands after I've committed the wager and
                stays visible while I answer, previewing wager × multiplier if correct. */}
            {wagerBoost && (
              <WagerBoostRoll
                face={wagerBoost.face}
                multiplier={wagerBoost.multiplier}
                wager={wagerBoost.wager}
              />
            )}
            {/* #232 v2: I've been tagged in to help answer — shown on my own phone. */}
            {taggedInFrom && <TagTeamCalledNotice fromName={taggedInFrom} />}
            {/* Auto-host (#91, D8): dispute a wrong ruling — the CTA after a ruling
                stands, or the open player vote. Lives above the clue so it stays
                reachable even after the board has moved on. */}
            {gamePhaseOf(state) !== 'GAME_OVER' && (
              <DisputePanel state={state} playerId={playerId} send={send} />
            )}
            {gamePhaseOf(state) === 'GAME_OVER' ? (
              <>
                <GameOver
                  players={state.players}
                  gameId={IS_PROD_DEPLOY ? state.gameId : undefined}
                  finalOutcome={state.jeopardy.finalOutcome}
                />
                {/* Post-game rating (#200, FR-16.1 / DI-D): players + host see it, not the
                    presentation screen. Once per game, skippable. */}
                <PostGameFeedback gameId={state.gameId} />
              </>
            ) : ddWager ? (
              // Hidden Peril: I uncovered it — wager before the prompt is shown. Arm Wager
              // Boost here; the die rolls once I submit (#232 v2, multiplier on a win).
              <>
                {powerups && (
                  <PowerUpHand
                    tokens={myTokens}
                    deck={powerups.config.deck.filter(
                      (k) => POWERUP_SPECS[k].timing === 'WAGER_STEP',
                    )}
                    windows={WAGER_ONLY_WINDOWS}
                    armed={myArmed}
                    targets={powerupTargets}
                    onPlay={playPowerup}
                  />
                )}
                <WagerInput
                  bounds={ddWager}
                  context={HIDDEN_PERIL}
                  onSubmit={(amount) => send({ type: 'submit_wager', amount })}
                />
              </>
            ) : finalWager ? (
              <>
                <div className="clue-panel" data-testid="final-category">
                  <div className="clue-panel__value">{FINAL_PERIL}</div>
                  <div className="clue-panel__prompt">
                    {state.jeopardy.final?.category}
                  </div>
                </div>
                {/* #232 v2: the Final-legal cards (Insurance, Wager Boost) during wagering. */}
                {powerups && (
                  <PowerUpHand
                    tokens={myTokens}
                    deck={powerups.config.deck.filter((k) => POWERUP_SPECS[k].finalLegal)}
                    windows={FINAL_WAGER_WINDOWS}
                    armed={myArmed}
                    targets={powerupTargets}
                    onPlay={playPowerup}
                  />
                )}
                <WagerInput
                  bounds={finalWager}
                  context={FINAL_PERIL}
                  onSubmit={(amount) => send({ type: 'submit_wager', amount })}
                />
              </>
            ) : typeFinalAnswer ? (
              <>
                <CluePanel
                  value={0}
                  prompt={state.jeopardy.packSnapshot.finalJeopardy.prompt}
                  categoryName={state.jeopardy.final?.category}
                />
                {state.timer.clueId && <TimerView timer={state.timer} />}
                <FinalAnswerInput
                  onSubmit={(text) => send({ type: 'submit_final_answer', text })}
                />
              </>
            ) : inFinal ? (
              gamePhaseOfOrNull(state) === 'FINAL_JUDGING' ? (
                <div className="stack" data-testid="final-judging">
                  {/* #344 (D344.11): keep the Final clue prompt visible through judging for
                      context. Once the correct response is revealed (first-correct /
                      last-judged) it flips in below (#344, D344.10 — flip). Until then the
                      prompt is shown and the response is held for suspense. */}
                  {finalResponse ? (
                    <div className="final-reveal-flip">
                      <FinalReveal
                        response={finalResponse}
                        category={state.jeopardy.final?.category}
                        resolvesAtEpochMs={finalRevealResolvesAt(state)}
                      />
                    </div>
                  ) : (
                    <CluePanel
                      value={0}
                      prompt={state.jeopardy.packSnapshot.finalJeopardy.prompt}
                      categoryName={state.jeopardy.final?.category}
                    />
                  )}
                  {/* #344 (FR-6.7): the currently-rated contestant's proposed answer. */}
                  {judgingAnswer && (
                    <FinalJudgingAnswer
                      name={judgingAnswer.name}
                      answer={judgingAnswer.answer}
                    />
                  )}
                  {finalAnswerBeingRead(state) && (
                    <p className="muted" data-testid="final-answer-reading">
                      Reading the answer aloud…
                    </p>
                  )}
                </div>
              ) : finalResponse ? (
                <>
                  {/* #205 (FR-6.6): the post-judging linger — show the correct response
                      on this device in place of the wait text. */}
                  <FinalReveal
                    response={finalResponse}
                    category={state.jeopardy.final?.category}
                    resolvesAtEpochMs={finalRevealResolvesAt(state)}
                  />
                  {finalAnswerBeingRead(state) && (
                    <p className="muted" data-testid="final-answer-reading">
                      Reading the answer aloud…
                    </p>
                  )}
                </>
              ) : (
                <p className="muted" data-testid="final-waiting">
                  {FINAL_PERIL} — waiting for the host…
                </p>
              )
            ) : reveal && clue ? (
              /* #192 (FR-4.10): the clue resolved — reveal the answer (unanswered)
                 or the award cue (correct) in place of the buzzer for the hold. */
              <>
                <ClueReveal
                  reveal={reveal}
                  response={revealedResponse(state)}
                  categoryName={categoryNameForClue(state, clue.id)}
                  value={clue.value}
                  awardedPlayerName={awardedName}
                />
                {/* Auto-host (#192): read the revealed answer aloud on this device. */}
                {answerBeingRead(state) === clue.id && (
                  <p className="muted" data-testid="answer-reading">
                    Reading the answer aloud…
                  </p>
                )}
              </>
            ) : clueOpen && clue ? (
              <>
                <CluePanel
                  value={clue.value}
                  prompt={clue.prompt}
                  categoryName={categoryNameForClue(state, clue.id)}
                />
                {/* #251 (FR-4.12): reserve the space above the buzzer so its position is
                    stable across the reading→open transition. The timer (which mounts at
                    buzzer-open) and the auto-host reading indicator both live inside this
                    fixed-height slot, so neither shifts the buzzer when it appears. */}
                <div className="buzz-stage__pre" data-testid="buzz-pre">
                  {state.timer.clueId && <TimerView timer={state.timer} />}
                  {/* Auto-host (#91): the clue is being read aloud; buzzers not yet open. */}
                  {clueBeingRead(state) === clue.id && (
                    <p className="muted" data-testid="clue-reading">
                      Reading the clue aloud…
                    </p>
                  )}
                </div>
                {clueState?.mode === 'DAILY_DOUBLE' ? (
                  <p className="muted" data-testid="dd-spectate">
                    {HIDDEN_PERIL} — only the uncovering player answers.
                  </p>
                ) : (
                  <>
                    <Buzzer
                      canBuzz={canBuzz}
                      active={iAmActive}
                      lockedOut={iAmLockedOut}
                      onBuzz={handleBuzz}
                      {...(armEarlySmash ? { onEarlyPress: registerEarlySmash } : {})}
                      {...(armLateRace ? { onLateRacePress: handleBuzz } : {})}
                    />
                    {/* #232 v2: explain a chilled/blocked buzzer right where the button is,
                        so a slowed (Brain Freeze) or bitten (Snake Bite) player sees WHY it
                        won't fire — instead of a live button that falsely says "You buzzed!". */}
                    {iAmFrozen && (
                      <BrainFreezeNotice
                        freezeMs={
                          freshFreeze?.freezeMs ??
                          snapshot.lastBrainFreeze?.freezeMs ??
                          750
                        }
                      />
                    )}
                    {iWasBitten && <SnakeBiteNotice />}
                    {/* #198 (FR-4.11): explain a buzzer-smash lockout so the player knows
                        why they can't answer. Announced to assistive tech. */}
                    {smashLockedOut && (
                      <p
                        className="buzzer-lockout-reason"
                        role="status"
                        aria-live="polite"
                        data-testid="smash-lockout-reason"
                      >
                        Locked out — you buzzed too early too many times. Wait for the
                        buzzer to open on the next clue.
                      </p>
                    )}
                  </>
                )}
                {/* Auto-host (#91): I hold the buzzer → capture my spoken answer (or
                    type it where STT is unavailable). Self-gates via authoritative state. */}
                <SpokenAnswer state={state} playerId={playerId} send={send} />
                {/* #232 (Game Night only): the power-up hand. Each card plays in its own
                    timing window (pre-buzz, a Steal rebound, or Tag-Team while answering);
                    Wager Boost lives in the wager branches. Not on a Daily Double clue. */}
                {powerups && clueState && clueState.mode !== 'DAILY_DOUBLE' && (
                  <>
                    {myArmed.includes('DOUBLE_UP') && <DoubleUpArmedBanner />}
                    {myArmed.includes('INSURANCE') && <InsuranceArmedBanner />}
                    <PowerUpHand
                      tokens={myTokens}
                      deck={powerups.config.deck}
                      windows={boardWindows}
                      armed={myArmed}
                      targets={powerupTargets}
                      onPlay={playPowerup}
                    />
                    {sneakPeek && (
                      <SneakPeekHintView
                        prefix={sneakPeek.prefix}
                        words={sneakPeek.words}
                      />
                    )}
                  </>
                )}
                {isJudging(state) && (
                  <p className="muted" data-testid="judging-indicator">
                    Judging your answer…
                  </p>
                )}
              </>
            ) : (
              <Board
                board={state.jeopardy.board}
                cluesById={state.jeopardy.packSnapshot.cluesById}
                {...(iSelectClues
                  ? {
                      onSelectClue: (clueId: string) =>
                        send({ type: 'select_clue', clueId }),
                    }
                  : {})}
              />
            )}
          </div>
          <div className="stack">
            <Scoreboard
              players={state.players}
              boardControlPlayerId={state.jeopardy.boardControlPlayerId}
              clueState={state.jeopardy.clueState}
              selfPlayerId={playerId ?? undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}
